import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthProvider';
import { supabase } from '@/lib/supabase';
import { fetchMember, signIn, signUpWeddingMember } from '@/lib/weddingApi';
import { LoadingScreen } from '@/components/platform/Guards';
import type { WeddingMember } from '@/types/wedding';

const SWATCHES = ['#c49a72', '#f2ece3', '#e7b7c2', '#8aa07f', '#d9d5cf'];

function landingFor(member: WeddingMember | null): string {
  if (!member) return '/pending';
  if (member.role === 'admin') return '/admin';
  if (member.status === 'approved') return '/my';
  return '/pending';
}

async function resolveLanding(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const uid = data.session?.user.id;
  if (!uid) return '/login';
  return landingFor(await fetchMember(uid));
}

export default function AuthPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { loading, session } = useAuth();
  const isSignup = location.pathname.startsWith('/signup');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (loading || !session) return;
    void (async () => {
      navigate(await resolveLanding(), { replace: true });
    })();
  }, [loading, session, navigate]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy) return;
    setError('');
    setBusy(true);
    try {
      if (isSignup) {
        await signUpWeddingMember({ email: email.trim(), password, name: name.trim(), phone: phone.trim() });
        await signIn(email, password);
      } else {
        await signIn(email, password);
      }
      navigate(await resolveLanding(), { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : '문제가 발생했어요. 다시 시도해 주세요.');
    } finally {
      setBusy(false);
    }
  };

  if (loading || session) return <LoadingScreen />;

  return (
    <div className="pf-page">
      <div className="pf-auth">
        <div className="pf-auth-copy">
          <p className="stamp" style={{ color: 'var(--accent2)' }}>
            Mobile Wedding Invitation
          </p>
          <h1>
            우리의 이야기로
            <br />
            청첩장을 <em className="accent">만들다</em>
          </h1>
          <p>
            신랑·신부 정보와 사진을 입력하면, 다섯 가지 디자인 중 하나를 골라 나만의 모바일 청첩장을
            완성할 수 있어요. 하객은 링크만 열면 됩니다.
          </p>
          <ul className="pf-feature-list">
            <li>
              <i className="ri-edit-2-line" aria-hidden="true" />
              예식 정보·인사말·계좌까지 한 번에 입력
            </li>
            <li>
              <i className="ri-image-add-line" aria-hidden="true" />
              사진을 올리면 갤러리로 자동 구성
            </li>
            <li>
              <i className="ri-palette-line" aria-hidden="true" />
              클래식부터 보태니컬까지, 디자인 5종
            </li>
          </ul>
          <div className="pf-theme-dots" aria-hidden="true">
            {SWATCHES.map((color) => (
              <span key={color} style={{ background: color }} />
            ))}
          </div>
        </div>

        <div className="pf-card">
          <div className="pf-tabs" role="tablist">
            <Link
              to="/login"
              className={`pf-tab ${isSignup ? '' : 'is-active'}`}
              role="tab"
              aria-selected={!isSignup}
            >
              로그인
            </Link>
            <Link
              to="/signup"
              className={`pf-tab ${isSignup ? 'is-active' : ''}`}
              role="tab"
              aria-selected={isSignup}
            >
              회원가입
            </Link>
          </div>

          <form className="pf-form" onSubmit={handleSubmit}>
            {isSignup ? (
              <div className="pf-row-2">
                <div className="pf-field">
                  <label className="pf-label" htmlFor="auth-name">
                    이름
                  </label>
                  <input
                    id="auth-name"
                    className="pf-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="홍길동"
                    autoComplete="name"
                    required
                  />
                </div>
                <div className="pf-field">
                  <label className="pf-label" htmlFor="auth-phone">
                    연락처
                  </label>
                  <input
                    id="auth-phone"
                    className="pf-input"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="010-0000-0000"
                    autoComplete="tel"
                    inputMode="tel"
                  />
                </div>
              </div>
            ) : null}

            <div className="pf-field">
              <label className="pf-label" htmlFor="auth-email">
                이메일
              </label>
              <input
                id="auth-email"
                className="pf-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                required
              />
            </div>

            <div className="pf-field">
              <label className="pf-label" htmlFor="auth-password">
                비밀번호
              </label>
              <input
                id="auth-password"
                className="pf-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="6자 이상 입력해 주세요"
                autoComplete={isSignup ? 'new-password' : 'current-password'}
                required
              />
            </div>

            {error ? (
              <div className="pf-alert err" role="alert">
                <i className="ri-error-warning-line" aria-hidden="true" />
                <span>{error}</span>
              </div>
            ) : null}

            <button type="submit" className="pf-btn primary block" disabled={busy}>
              {busy ? (
                <>
                  <i className="ri-loader-4-line spin-anim" aria-hidden="true" />
                  처리 중이에요…
                </>
              ) : isSignup ? (
                <>
                  <i className="ri-user-add-line" aria-hidden="true" />
                  가입 신청하기
                </>
              ) : (
                <>
                  <i className="ri-login-circle-line" aria-hidden="true" />
                  로그인
                </>
              )}
            </button>

            {isSignup ? (
              <div className="pf-alert info">
                <i className="ri-information-line" aria-hidden="true" />
                <span>
                  별도의 이메일 인증은 없어요. 가입 후 <strong>운영자 승인</strong>이 완료되면 청첩장을
                  만들 수 있어요.
                </span>
              </div>
            ) : (
              <p className="pf-muted" style={{ textAlign: 'center' }}>
                아직 계정이 없으신가요? <Link to="/signup" style={{ color: 'var(--accent)' }}>회원가입</Link>
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}